import { Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Field = ({ field, value, onChange, tid }) => {
  const id = `kyc-${tid}`;
  if (field.type === "checkbox") {
    return (
      <label className="flex items-center gap-3 rounded-lg border border-teal-200 bg-teal-50/50 p-3 text-sm md:col-span-2">
        <Checkbox data-testid={id} checked={!!value} onCheckedChange={(v) => onChange(!!v)} /> {field.label}
      </label>
    );
  }
  return (
    <div className={`space-y-1.5 ${field.span === 2 ? "md:col-span-2" : ""}`}>
      <Label className="text-xs font-medium text-slate-600">{field.label}{field.req && <span className="text-red-500"> *</span>}</Label>
      {field.type === "select" ? (
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={id}><SelectValue placeholder="Seleccionar" /></SelectTrigger>
          <SelectContent>{field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input data-testid={id} type={field.type} maxLength={field.maxLength} value={value || ""}
          onChange={(e) => onChange(["rfc", "curp"].includes(field.name) ? e.target.value.toUpperCase() : e.target.value)} />
      )}
    </div>
  );
};

const Grid = ({ section, value, onChange, prefix }) => (
  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
    {section.fields.map((fd) => (
      <Field key={fd.name} field={fd} tid={`${prefix}-${fd.name}`} value={value?.[fd.name]} onChange={(v) => onChange({ ...(value || {}), [fd.name]: v })} />
    ))}
  </div>
);

export const KycSection = ({ section, value, onChange }) => {
  const items = section.repeat ? (value?.length ? value : [{}]) : null;
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`kyc-section-${section.key}`}>
      <div className="mb-5 border-l-4 border-teal-500 pl-3">
        <h3 className="text-lg font-semibold text-slate-900">{section.title}</h3>
        {section.note && <p className="mt-0.5 text-xs text-slate-500">{section.note}</p>}
      </div>
      {!section.repeat && <Grid section={section} value={value} onChange={onChange} prefix={section.key} />}
      {items && (
        <div className="space-y-4">
          {items.map((it, i) => (
            <div key={i} className="relative rounded-xl border border-dashed border-slate-200 p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">#{i + 1}</span>
                {items.length > 1 && (
                  <button type="button" data-testid={`kyc-${section.key}-remove-${i}`} onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                )}
              </div>
              <Grid section={section} value={it} prefix={`${section.key}-${i}`} onChange={(v) => onChange(items.map((x, j) => (j === i ? v : x)))} />
            </div>
          ))}
          {items.length < section.repeat && (
            <button type="button" data-testid={`kyc-${section.key}-add`} onClick={() => onChange([...items, {}])} className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-900">
              <Plus className="h-4 w-4" /> Agregar registro
            </button>
          )}
        </div>
      )}
    </section>
  );
};

const show = (v) => (v === true ? "Sí" : v || "—");

export const KycReadOnly = ({ section, value }) => {
  const items = section.repeat ? (value || []).filter((x) => Object.values(x || {}).some(Boolean)) : [value || {}];
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <h3 className="mb-4 border-l-4 border-teal-500 pl-3 text-base font-semibold text-slate-900">{section.title}</h3>
      {items.length === 0 && <p className="text-sm text-slate-400">Sin registros</p>}
      {items.map((it, i) => (
        <dl key={i} className={`grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-3 ${i ? "mt-4 border-t border-slate-100 pt-4" : ""}`}>
          {section.fields.map((fd) => (
            <div key={fd.name}><dt className="text-xs text-slate-500">{fd.label}</dt><dd className="text-sm text-slate-900 break-words">{show(it?.[fd.name])}</dd></div>
          ))}
        </dl>
      ))}
    </section>
  );
};
